import { memo, useCallback, useEffect, useMemo, useState, type ChangeEvent, type CSSProperties, type RefObject } from 'react'
import { AlertTriangle, CalendarRange, Check, Clock3, Eye, RotateCcw } from 'lucide-react'
import { gridGenerationsApi, gridTemplatesApi } from '../../lib/api.js'
import { TemplateHourAxis, templateCanvasVars } from './TemplateHourAxis.js'
import type { GenerationScope, GridGeneration, ScheduleGrid, TemplateProposal } from '../../types/domain.js'
import { substitutionOriginId } from '../../lib/mirrorSubstitution.js'
import { generationRange } from '../../lib/generationRange.js'

const DAYS = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM']

function localDateISO(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function mondayOf(value: string) {
  const date = new Date(`${value}T12:00:00`)
  const offset = (date.getDay() + 6) % 7
  date.setDate(date.getDate() - offset)
  return localDateISO(date)
}

function formatDate(value: string) {
  return new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function proposalTitle(proposal: TemplateProposal): string {
  return proposal.programTitle
    ?? proposal.pendingPremiere?.title
    // Rien de choisi : l'intention du créneau dit au moins ce qui était prévu.
    ?? (proposal.intent.label ? `Non résolu · ${proposal.intent.label}` : 'Créneau non résolu')
}

function roleLabel(proposal: TemplateProposal) {
  if (proposal.itemType === 'LIVE') return `DIRECT${proposal.liveSourceName ? ` · ${proposal.liveSourceName}` : ''}`
  if (proposal.pendingPremiere) return 'INÉDIT À CRÉER'
  if (proposal.pendingPremiereReplayOf) return 'REPRISE DE L’INÉDIT'
  if (proposal.airingKind === 'PREMIERE') return 'INÉDIT'
  if (proposal.ruleType === 'PREMIERE_RERUN') return 'REDIFFUSION DE L’INÉDIT'
  if (proposal.ruleType === 'DESCENDING_RERUN') return 'DESCENTE ANTICHRONOLOGIQUE'
  if (proposal.ruleType === 'CURATED') return 'CRÉNEAU LIBRE'
  if (proposal.airingKind === 'RERUN') return 'REDIFFUSION'
  return 'PROGRAMME FIXE'
}

function proposalTone(proposal: TemplateProposal) {
  if (proposal.blocking) return 'blocking'
  if (proposal.itemType === 'LIVE') return 'live'
  if (proposal.airingKind === 'RERUN') return 'rerun'
  if (proposal.airingKind === 'PREMIERE') return 'anchor'
  return 'standard'
}

function proposalKey(proposal: TemplateProposal) {
  return proposal.proposalId ?? `${proposal.slotId}:${proposal.startTime}`
}

function isImmutableConflict(proposal: TemplateProposal) {
  return proposal.conflicts.some(item => ['ON_AIR', 'DONE'].includes(item.status))
}

function hasInternalOverlap(proposal: TemplateProposal) {
  return proposal.warnings.some(warning => warning.startsWith('Chevauchement'))
}

export const TemplatePreviewWorkspace = memo(function TemplatePreviewWorkspace({
  template,
  channelId,
  canManage,
  hourHeight,
  scrollRef,
  onHistoryChanged,
}: {
  template: ScheduleGrid
  channelId: string
  canManage: boolean
  hourHeight: number
  scrollRef: RefObject<HTMLDivElement>
  onHistoryChanged?: () => void
}) {
  const [targetDate, setTargetDate] = useState(mondayOf(localDateISO()))
  const [scope, setScope] = useState<GenerationScope>('WEEK')
  const [generationId, setGenerationId] = useState<string | null>(null)
  const [templateVersion, setTemplateVersion] = useState(template.version)
  const [proposals, setProposals] = useState<TemplateProposal[]>([])
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null)
  const [generateReruns, setGenerateReruns] = useState(true)
  const [replaceConflicts, setReplaceConflicts] = useState(false)
  const [busy, setBusy] = useState<'preview' | 'apply' | 'rollback' | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [history, setHistory] = useState<GridGeneration[]>([])

  const loadHistory = useCallback(async () => {
    const recent = await gridGenerationsApi.recent(channelId)
    setHistory(recent.filter(item => item.templateId === template.id))
  }, [channelId, template.id])

  useEffect(() => {
    loadHistory().catch(console.error)
  }, [loadHistory])

  const preview = useCallback(async () => {
    setBusy('preview')
    setMessage(null)
    try {
      const result = await gridTemplatesApi.preview(template.id, { targetDate, scope })
      setGenerationId(result.generationId)
      setTemplateVersion(result.templateVersion)
      setProposals(result.proposals)
      const firstSelected = result.proposals.find(item => item.blocking || item.conflicts.length > 0) ?? result.proposals[0]
      setSelectedSlotId(firstSelected ? proposalKey(firstSelected) : null)
    } catch (error: any) {
      setMessage(error.message)
    } finally {
      setBusy(null)
    }
  }, [scope, targetDate, template.id])

  const apply = useCallback(async () => {
    if (!generationId) return
    setBusy('apply')
    setMessage(null)
    try {
      const decisions = proposals.map(proposal => {
        const skipByOption = !generateReruns && proposal.airingKind === 'RERUN'
        const mustSkip = proposal.blocking || hasInternalOverlap(proposal) || isImmutableConflict(proposal)
        return {
          slotId: proposal.slotId,
          proposalId: proposal.proposalId,
          action: skipByOption || mustSkip ? 'SKIP' as const : 'APPLY' as const,
          conflictAction: replaceConflicts ? 'REPLACE' as const : 'KEEP' as const,
        }
      })
      const result = await gridGenerationsApi.apply(generationId, decisions)
      setGenerationId(null)
      setMessage(`${result.created} item(s) posé(s) sur la grille de diffusion${result.replaced ? ` · ${result.replaced} remplacé(s)` : ''}.`)
      await loadHistory()
      onHistoryChanged?.()
    } catch (error: any) {
      setMessage(error.message)
    } finally {
      setBusy(null)
    }
  }, [generateReruns, generationId, loadHistory, onHistoryChanged, proposals, replaceConflicts])

  const latestApplied = history.find(item => ['APPLIED', 'PARTIALLY_ROLLED_BACK'].includes(item.status)) ?? null
  const rollback = useCallback(async () => {
    if (!latestApplied || !window.confirm(`Annuler le chargement v${latestApplied.templateVersion} ? Les éléments déjà diffusés ou à l’antenne seront protégés.`)) return
    setBusy('rollback')
    setMessage(null)
    try {
      const result = await gridGenerationsApi.rollback(latestApplied.id)
      setMessage(`${result.deleted} item(s) retiré(s), ${result.restored} restauré(s), ${result.preserved} protégé(s).`)
      await loadHistory()
      onHistoryChanged?.()
    } catch (error: any) {
      setMessage(error.message)
    } finally {
      setBusy(null)
    }
  }, [latestApplied, loadHistory, onHistoryChanged])

  const range = generationRange(targetDate, scope)
  const selected = useMemo(() => proposals.find(proposal => proposalKey(proposal) === selectedSlotId) ?? null, [proposals, selectedSlotId])
  const visibleProposals = useMemo(
    () => proposals.filter(proposal => generateReruns || proposal.airingKind !== 'RERUN'),
    [generateReruns, proposals],
  )
  const conflictProposals = useMemo(() => proposals.filter(item => item.blocking || item.conflicts.length > 0 || item.warnings.length > 0), [proposals])
  const conflictCount = conflictProposals.length
  // Créneaux sans contenu : ni programme choisi, ni inédit réservé, ni tête
  // reprise. C'est le reste à traiter avant de poser le modèle.
  const unresolvedCount = useMemo(() => proposals.filter(proposal => {
    if (proposal.programId || proposal.pendingPremiere) return false
    const originId = substitutionOriginId(proposal)
    const origin = originId ? proposals.find(item => item.proposalId === originId) : undefined
    return !origin || !(origin.programId || origin.pendingPremiere)
  }).length, [proposals])

  const handleTargetDateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setTargetDate(event.target.value)
    setGenerationId(null)
  }, [])
  const handleScopeChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    setScope(event.target.value as GenerationScope)
    setGenerationId(null)
  }, [])
  const handleProposalSelect = useCallback((id: string) => setSelectedSlotId(id), [])

  return (
    <div className="template-preview">
      <section className="template-preview__main">
        <header className="template-preview__header">
          <div>
            <span className="template-preview__eyebrow">PRÉVISUALISATION · V{templateVersion}</span>
            <h2>{template.name}</h2>
            <p>{proposals.length
              ? `${visibleProposals.length} propositions calculées · ${unresolvedCount} non résolu(s) · ${conflictCount} point(s) à contrôler`
              : 'Choisissez une plage puis lancez la simulation.'}</p>
          </div>
          <button type="button" className="tp-button" disabled={busy !== null || !latestApplied} onClick={rollback}>
            <RotateCcw size={14} aria-hidden="true" />
            {latestApplied ? `Rollback v${latestApplied.templateVersion}` : 'Aucun rollback'}
          </button>
        </header>
        {message && <div className="tp-feedback templates-message" role="status">{message}</div>}
        <div ref={scrollRef} className="templates-canvas-scroll template-preview__canvas-scroll">
          <div className="templates-canvas template-preview__canvas" style={templateCanvasVars(hourHeight)}>
            <TemplateHourAxis hourHeight={hourHeight} />
            {DAYS.map((day, dayOffset) => (
              <section key={day} className="template-day">
                <header className="template-day__header">{day}</header>
                <div className="template-day__timeline">
                  {visibleProposals.filter(proposal => proposal.dayOffset === dayOffset).map(proposal => (
                    <ProposalBlock
                      key={proposalKey(proposal)}
                      proposal={proposal}
                      selected={selectedSlotId === proposalKey(proposal)}
                      onSelect={handleProposalSelect}
                      hourHeight={hourHeight}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      <aside className="template-preview__inspector">
        {selected && <ProposalDetails proposal={selected} />}

        <section className="template-generation-card">
          <span className="template-panel-title">GÉNÉRATION</span>
          <div className="template-generation-range">
            <CalendarRange size={18} aria-hidden="true" />
            <div>
              <span>PLAGE CIBLE</span>
              <strong>{formatDate(range.start)} → {formatDate(range.end)}</strong>
              <small>{range.dayCount} jour{range.dayCount > 1 ? 's' : ''} · {visibleProposals.length || '—'} item(s) proposé(s)</small>
            </div>
          </div>
          <label className="template-field">
            <span className="tp-field-label">Date de départ</span>
            <input className="tp-input tp-input--data" type="date" value={targetDate} onChange={handleTargetDateChange} />
          </label>
          <label className="template-field">
            <span className="tp-field-label">Portée</span>
            <select className="tp-select" value={scope} onChange={handleScopeChange}>
              <option value="WEEK">Semaine</option>
              <option value="DAY">Jour sélectionné</option>
            </select>
          </label>
        </section>

        <fieldset className="template-generation-options">
          <legend>OPTIONS</legend>
          <OptionCheckbox checked={generateReruns} onChange={setGenerateReruns} label="Générer les rediffusions liées" />
          <OptionCheckbox checked={replaceConflicts} onChange={setReplaceConflicts} label="Remplacer les items modifiables" />
          <p className="template-generation-options__note">
            Les trous laissés entre les créneaux se comblent sur la grille de diffusion, par jingle ou infoservice.
          </p>
        </fieldset>

        <section className="template-conflicts" aria-label="Conflits détectés">
          <div className="template-conflicts__title">
            <span>CONFLITS</span>
            <strong>{conflictCount}</strong>
          </div>
          {conflictProposals.slice(0, 5).map(proposal => (
            <button key={proposalKey(proposal)} type="button" className={`template-conflict is-${proposalTone(proposal)}`} onClick={() => handleProposalSelect(proposalKey(proposal))}>
              <AlertTriangle size={15} aria-hidden="true" />
              <span>
                <strong>{new Date(proposal.startTime).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })} · {new Date(proposal.startTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</strong>
                <small>{proposal.blocking ? 'Créneau bloquant' : proposal.conflicts[0]?.title ?? proposal.warnings[0]}</small>
              </span>
            </button>
          ))}
          {proposals.length > 0 && conflictCount === 0 && <div className="template-conflicts__empty"><Check size={16} aria-hidden="true" /> Aucun conflit détecté</div>}
          {proposals.length === 0 && <div className="template-conflicts__empty"><Eye size={16} aria-hidden="true" /> Lancez une simulation</div>}
        </section>

        <div className="template-generation-actions">
          <button type="button" className="tp-button" disabled={busy !== null} data-state={busy === 'preview' ? 'loading' : undefined} onClick={preview}>
            <Eye size={15} aria-hidden="true" />
            {proposals.length
              ? proposals.some(proposal => proposal.ruleType === 'SMART_RERUN' || proposal.ruleType === 'FRESH_RERUN')
                ? 'Recalculer avec d’autres épisodes'
                : 'Recalculer'
              : 'Prévisualiser'}
          </button>
          <button type="button" className="tp-button tp-button--primary" disabled={!canManage || !generationId || busy !== null} data-state={busy === 'apply' ? 'loading' : undefined} onClick={apply}>
            <Check size={15} aria-hidden="true" />
            Appliquer le modèle
          </button>
          <p>Les items déjà diffusés ou à l’antenne sont toujours protégés.</p>
        </div>
      </aside>
    </div>
  )
})

const OptionCheckbox = memo(function OptionCheckbox({ checked, onChange, label }: { checked: boolean; onChange: (value: boolean) => void; label: string }) {
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked), [onChange])
  return <label className="template-generation-option"><input type="checkbox" checked={checked} onChange={handleChange} /><span>{label}</span></label>
})


const ProposalBlock = memo(function ProposalBlock({ proposal, selected, onSelect, hourHeight }: { proposal: TemplateProposal; selected: boolean; onSelect: (id: string) => void; hourHeight: number }) {
  // Le bloc couvre l'unité complète, habillage compris : c'est cette emprise-là
  // qui occupe l'antenne et qui sert au calcul des conflits.
  const leadSec = (proposal.preRoll?.durationSec ?? 0) + (proposal.junctionJingle?.durationSec ?? 0)
  const start = new Date(new Date(proposal.startTime).getTime() - leadSec * 1000)
  const unitDurationSec = leadSec + proposal.durationSec + (proposal.postRoll?.durationSec ?? 0)
  const top = (start.getHours() + start.getMinutes() / 60) * hourHeight
  const height = Math.max(1, unitDurationSec / 3600 * hourHeight)
  const style = {
    '--proposal-top': `${top}px`,
    '--proposal-height': `${height}px`,
  } as CSSProperties
  const handleClick = useCallback(() => onSelect(proposalKey(proposal)), [onSelect, proposal])

  return (
    <button type="button" onClick={handleClick} className={`template-proposal is-${proposalTone(proposal)}${selected ? ' is-selected' : ''}`} style={style} title={`${proposalTitle(proposal)} · ${Math.round(proposal.durationSec / 60)} min`}>
      <span className="template-proposal__title">{proposalTitle(proposal)}</span>
      <span className="template-proposal__meta">{Math.round(proposal.durationSec / 60)} min · {roleLabel(proposal)}</span>
    </button>
  )
})

const ProposalDetails = memo(function ProposalDetails({ proposal }: { proposal: TemplateProposal }) {
  return (
    <section className="template-proposal-details">
      <div>
        <span className="template-panel-title">SÉLECTION</span>
        <h2>{proposalTitle(proposal)}</h2>
        <p><Clock3 size={13} aria-hidden="true" /> {new Date(proposal.startTime).toLocaleString('fr-FR')}</p>
      </div>
      <div className="template-proposal-details__reason">{proposal.reason}</div>
      {!proposal.programTitle && !proposal.pendingPremiere && proposal.intent.label && (
        <p>Contenu prévu par le créneau : {proposal.intent.label}</p>
      )}
      {(proposal.junctionJingle || proposal.preRoll || proposal.postRoll) && <p>
        {proposal.junctionJingle && <>Liaison : {proposal.junctionJingle.title} · {Math.round(proposal.junctionJingle.durationSec)} s<br /></>}
        {proposal.preRoll && <>Avant : {proposal.preRoll.title} · {Math.round(proposal.preRoll.durationSec)} s<br /></>}
        {proposal.postRoll && <>Après : {proposal.postRoll.title} · {Math.round(proposal.postRoll.durationSec)} s</>}
      </p>}
      {proposal.itemType === 'PROGRAM' && <p>Première diffusion : {proposal.firstAiredAt ? new Date(proposal.firstAiredAt).toLocaleDateString('fr-FR') : 'jamais'}<br />Dernière diffusion : {proposal.lastAiredAt ? new Date(proposal.lastAiredAt).toLocaleDateString('fr-FR') : 'jamais'}<br />Rediffusions : {proposal.rerunCount}</p>}
      {proposal.warnings.map(warning => <div key={warning} className="template-proposal-details__warning">{warning}</div>)}
      {proposal.conflicts.map(conflict => <div key={conflict.id} className="template-proposal-details__warning">Conflit avec {conflict.title} · {conflict.status}</div>)}
      {proposal.blocking && <div className="tp-feedback template-proposal-details__blocking">Ce créneau sera ignoré tant qu’il reste bloquant.</div>}
    </section>
  )
})
